import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useNetworkStore } from "../../stores/network";
import { useTxDetail } from "../../api/hooks";
import { formatFJ, formatFJPerMana } from "../../lib/format";
import { useMyTxs } from "../../stores/my-txs";
import { useLabeledAddresses } from "../../hooks/useAddressResolver";
import {
  PageContainer,
  PageTitle,
  Card,
  SectionTitle,
  Table,
  TableWrapper,
  Mono,
  Loading,
  Flex,
  Badge,
  Button,
} from "../../components/ui";
import { HexDisplay } from "../../components/HexDisplay";
import { theme } from "../../lib/theme";
import {
  SnapshotableFingerprint,
  FingerprintCompare,
} from "../../components/TxFingerprint";
import { abbreviateHex } from "../../components/TxTable";
import { AddToClipboardButton } from "../../components/AddToClipboardButton";

import {
  formatSmallNumber,
  similarTxToVector,
  formatDelta,
  FEATURE_LABELS,
} from "./utils";
import {
  Field,
  FieldLabel,
  FieldValue,
  FeatureRow,
  FeatureName,
  FeatureValue,
  TabBar,
  Tab,
  TopColumns,
  TopColumnLeft,
  TopColumnRight,
  FingerprintInner,
  FingerprintChartCol,
  FingerprintValuesCol,
  ValuesToggle,
  FingerprintValuesMobile,
  SimilarGrid,
  SimilarTxCard,
  SimilarTxHeader,
} from "./styled";
import { Collapsible } from "./Collapsible";
import { SlotTimelines } from "./SlotTimelines";
import { PublicCallsSection } from "./PublicCallsSection";
import { PublicAddressesSection } from "./PublicAddressesSection";
import {
  L2ToL1MessagesSection,
  PrivateLogsSection,
  PublicLogsSection,
  ContractClassLogsSection,
} from "./LogSections";

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "finalized"
      ? theme.colors.success
      : status === "proven"
        ? theme.colors.success
        : status === "checkpointed"
          ? theme.colors.accent
          : status === "proposed"
            ? theme.colors.accent
            : status === "dropped"
              ? theme.colors.danger
              : theme.colors.warning;
  return <Badge color={color}>{status}</Badge>;
}

export function TxDetail() {
  const { hash } = useParams<{ hash: string }>();
  const { selectedNetwork } = useNetworkStore();
  const { data, isLoading, isError } = useTxDetail(selectedNetwork, hash ?? "");
  const { isTracked, add, remove } = useMyTxs();
  const labeledAddresses = useLabeledAddresses();
  const [activeTab, setActiveTab] = useState<"details" | "similar" | "public">(
    "details",
  );
  const [valuesCollapsed, setValuesCollapsed] = useState(
    () => window.innerWidth < 1700,
  );
  const sortedSimilarTxs = useMemo(
    () =>
      data
        ? [...data.similarTxs].sort(
            (a, b) => (b.outlierScore ?? 0) - (a.outlierScore ?? 0),
          )
        : [],
    [data],
  );

  if (isLoading) return <Loading />;

  if (isError || !data) {
    return (
      <PageContainer>
        <Card>
          <SectionTitle>Transaction not found</SectionTitle>
          <p style={{ color: theme.colors.textMuted }}>
            No transaction with hash <Mono>{hash}</Mono> exists on this network.
          </p>
        </Card>
      </PageContainer>
    );
  }

  const {
    tx,
    featureVector,
    noteHashes,
    nullifiers,
    publicCalls,
    privacySet,
    l2ToL1Messages,
    privateLogDetails,
    publicLogDetails,
    contractClassLogDetails,
    publicAddresses,
    feePayerPct,
  } = data;
  const tracked = isTracked(tx.txHash);

  const privacySetPct =
    privacySet && privacySet.totalTxsAnalyzed > 0
      ? (privacySet.clusterSize / privacySet.totalTxsAnalyzed) * 100
      : 0;

  return (
    <PageContainer>
      {/* Wraps: the title and the three action buttons are all unshrinkable, and together they are
          wider than a narrow phone — without this the last button falls off the page. */}
      <Flex justify="space-between" wrap style={{ marginBottom: theme.spacing.md }}>
        <PageTitle style={{ marginBottom: 0 }}>Transaction</PageTitle>
        <Flex gap="8px" wrap>
          <AddToClipboardButton hash={tx.txHash} />
          <a
            href={`/api/networks/${selectedNetwork}/txs/${tx.txHash}/raw`}
            download={`tx-${tx.txHash.slice(0, 10)}.json`}
            style={{ textDecoration: "none" }}
          >
            <Button>Download Raw TX</Button>
          </a>
          <Button
            variant={tracked ? "danger" : "primary"}
            onClick={() => (tracked ? remove(tx.txHash) : add(tx.txHash))}
          >
            {tracked ? "Untrack" : "Track as Mine"}
          </Button>
        </Flex>
      </Flex>

      {/* Overview + Fingerprint side-by-side */}
      <TopColumns>
        <TopColumnLeft>
          <Card style={{ flex: 1 }}>
            <Field>
              <FieldLabel>Hash</FieldLabel>
              <FieldValue>
                <HexDisplay address={tx.txHash} link={false} />
              </FieldValue>
            </Field>
            <Flex gap="24px" wrap>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>
                  <StatusBadge status={tx.status} />
                </FieldValue>
              </Field>
              {tx.blockNumber != null && (
                <Field>
                  <FieldLabel>Block</FieldLabel>
                  <FieldValue>{tx.blockNumber.toLocaleString()}</FieldValue>
                </Field>
              )}
              {tx.txIndex != null && (
                <Field>
                  <FieldLabel>Index</FieldLabel>
                  <FieldValue>{tx.txIndex}</FieldValue>
                </Field>
              )}
              {tx.executionResult != null && (
                <Field>
                  <FieldLabel>Execution Result</FieldLabel>
                  <FieldValue>
                    {tx.executionResult === "success" ? (
                      <Badge color={theme.colors.success}>Success</Badge>
                    ) : (
                      <Badge color={theme.colors.danger}>
                        {tx.executionResult.replace(/_/g, " ")}
                      </Badge>
                    )}
                  </FieldValue>
                </Field>
              )}
              {tx.error && (
                <Field>
                  <FieldLabel>Error</FieldLabel>
                  <FieldValue style={{ color: theme.colors.danger }}>
                    {tx.error}
                  </FieldValue>
                </Field>
              )}
              {privacySet ? (
                <>
                  <Field>
                    <FieldLabel>Privacy Set Size</FieldLabel>
                    <FieldValue>
                      <Link
                        to={`/${selectedNetwork}/privacy-sets?cluster=${privacySet.clusterId}`}
                        style={{ textDecoration: "none" }}
                        title="View this privacy set's cluster"
                      >
                        <Badge
                          color={
                            privacySet.clusterSize === 1
                              ? theme.colors.danger
                              : privacySet.clusterSize <= 5
                                ? theme.colors.warning
                                : theme.colors.success
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {privacySet.clusterSize === 1
                            ? "1 (unique)"
                            : `${privacySet.clusterSize.toLocaleString()} txs`}
                        </Badge>
                      </Link>
                    </FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>% of Network</FieldLabel>
                    <FieldValue>
                      {privacySetPct < 0.1
                        ? "<0.1%"
                        : `${privacySetPct.toFixed(1)}%`}
                      <span
                        style={{
                          color: theme.colors.textMuted,
                          fontSize: theme.fontSize.xs,
                          marginLeft: "4px",
                        }}
                      >
                        ({privacySet.totalTxsAnalyzed.toLocaleString()} total)
                      </span>
                    </FieldValue>
                  </Field>
                  {privacySet.outlierScore != null && (
                    <Field>
                      <FieldLabel>Outlier Score</FieldLabel>
                      <FieldValue>
                        {(privacySet.outlierScore * 100).toFixed(1)}%
                      </FieldValue>
                    </Field>
                  )}
                </>
              ) : (
                <Field>
                  <FieldLabel>Privacy Set</FieldLabel>
                  <FieldValue>
                    <Badge color={theme.colors.textMuted}>
                      Not yet analyzed
                    </Badge>
                  </FieldValue>
                </Field>
              )}
            </Flex>
            {tx.expirationTimestamp != null && tx.expirationTimestamp !== 0 && (
              <Field>
                <FieldLabel>Expiration</FieldLabel>
                <FieldValue>
                  {new Date(tx.expirationTimestamp * 1000).toLocaleString()}
                  {tx.anchorBlockTimestamp != null &&
                    tx.anchorBlockTimestamp !== 0 && (
                      <span
                        style={{
                          color: theme.colors.textMuted,
                          fontSize: theme.fontSize.xs,
                          marginLeft: "8px",
                        }}
                      >
                        (delta:{" "}
                        {formatDelta(
                          tx.expirationTimestamp - tx.anchorBlockTimestamp,
                        )}
                        )
                      </span>
                    )}
                </FieldValue>
              </Field>
            )}
            <Flex gap="24px" wrap>
              {tx.firstSeenAt && (
                <Field>
                  <FieldLabel>First seen</FieldLabel>
                  <FieldValue title={new Date(tx.firstSeenAt).toISOString()}>
                    {new Date(tx.firstSeenAt).toLocaleString()}
                  </FieldValue>
                </Field>
              )}
              {tx.proposedAt && (
                <Field>
                  <FieldLabel>Proposed at</FieldLabel>
                  <FieldValue title={new Date(tx.proposedAt).toISOString()}>
                    {new Date(tx.proposedAt).toLocaleString()}
                    {tx.firstSeenAt && (
                      <span style={{ color: theme.colors.textMuted, fontSize: theme.fontSize.xs, marginLeft: 8 }}>
                        (+{Math.round((new Date(tx.proposedAt).getTime() - new Date(tx.firstSeenAt).getTime()) / 1000)}s in mempool)
                      </span>
                    )}
                  </FieldValue>
                </Field>
              )}
            </Flex>
            <Field>
              <FieldLabel>Fee Payer</FieldLabel>
              <FieldValue>
                <HexDisplay
                  address={tx.feePayer}
                />
                <span
                  style={{
                    color: theme.colors.textMuted,
                    fontSize: theme.fontSize.xs,
                    marginLeft: "8px",
                  }}
                >
                  ({feePayerPct < 0.1 ? "<0.1" : feePayerPct.toFixed(1)}% of
                  network txs)
                </span>
              </FieldValue>
            </Field>
            {tx.actualFee && (
              <Field>
                <FieldLabel>Actual Fee</FieldLabel>
                <FieldValue>
                  {formatFJ(tx.actualFee)}
                  {data.feePricingData && (
                    <span style={{ marginLeft: 8, opacity: 0.7 }}>
                      ≈ {formatSmallNumber(data.feePricingData.costUsd)} USD (
                      {formatSmallNumber(data.feePricingData.costEth)} ETH @ $
                      {data.feePricingData.ethUsdPrice.toLocaleString()}/ETH)
                      {data.feePricingData.snapshotAgeSeconds != null &&
                        data.feePricingData.snapshotAgeSeconds > 10800 && (
                          <span
                            title={`Stale pricing — data is ${Math.round(data.feePricingData.snapshotAgeSeconds / 3600)}h old`}
                            style={{ marginLeft: 6, color: "#ffc66c", fontWeight: 600, opacity: 1, cursor: "help" }}
                          >
                            ⚠
                          </span>
                        )}
                    </span>
                  )}
                </FieldValue>
              </Field>
            )}
          </Card>
        </TopColumnLeft>

        {featureVector && (
          <TopColumnRight>
            <Card style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}>
              <FieldLabel>Transaction Fingerprint</FieldLabel>
              <ValuesToggle
                collapsed={valuesCollapsed}
                onClick={() => setValuesCollapsed((v) => !v)}
              >
                {valuesCollapsed ? "Show Values \u25B6" : "\u25C0 Hide Values"}
              </ValuesToggle>
              <FingerprintInner>
                <FingerprintChartCol>
                  <SnapshotableFingerprint
                    vector={featureVector}
                    showLabels
                    label={tx.txHash.slice(0, 10)}
                  />
                </FingerprintChartCol>
                <FingerprintValuesCol collapsed={valuesCollapsed}>
                  {FEATURE_LABELS.map((label, i) => (
                    <FeatureRow key={i}>
                      <FeatureName>{label}</FeatureName>
                      <FeatureValue>
                        {i === 15 ? (
                          <HexDisplay address={String(featureVector[i])} mode="label" />
                        ) : (i === 8 || i === 9) ? (
                          `${formatFJPerMana(featureVector[i])} FJ/mana`
                        ) : i === 12 ? (
                          featureVector[i] ? "Yes" : "No"
                        ) : typeof featureVector[i] === "number" ? (
                          (featureVector[i] as number).toLocaleString()
                        ) : (
                          String(featureVector[i])
                        )}
                      </FeatureValue>
                    </FeatureRow>
                  ))}
                </FingerprintValuesCol>
              </FingerprintInner>
              <FingerprintValuesMobile>
                <Collapsible title="Raw Values" defaultOpen={false}>
                  {FEATURE_LABELS.map((label, i) => (
                    <FeatureRow key={i}>
                      <FeatureName>{label}</FeatureName>
                      <FeatureValue>
                        {i === 15 ? (
                          <HexDisplay address={String(featureVector[i])} mode="label" />
                        ) : (i === 8 || i === 9) ? (
                          `${formatFJPerMana(featureVector[i])} FJ/mana`
                        ) : i === 12 ? (
                          featureVector[i] ? "Yes" : "No"
                        ) : typeof featureVector[i] === "number" ? (
                          (featureVector[i] as number).toLocaleString()
                        ) : (
                          String(featureVector[i])
                        )}
                      </FeatureValue>
                    </FeatureRow>
                  ))}
                </Collapsible>
              </FingerprintValuesMobile>
            </Card>
          </TopColumnRight>
        )}
      </TopColumns>

      {/* Tabs: Similar Txs / Tx Effects */}
      <TabBar>
        {featureVector && sortedSimilarTxs.length > 0 && (
          <Tab
            active={activeTab === "similar"}
            onClick={() => setActiveTab("similar")}
          >
            Similar Txs ({sortedSimilarTxs.length})
          </Tab>
        )}
        <Tab
          active={activeTab === "details"}
          onClick={() => setActiveTab("details")}
        >
          Tx Effects
        </Tab>
        <Tab
          active={activeTab === "public"}
          onClick={() => setActiveTab("public")}
        >
          Public Activity ({publicAddresses.length + publicCalls.length})
        </Tab>
      </TabBar>

      {activeTab === "details" && (
        <>
          {noteHashes.length > 0 && (
            <Collapsible
              title="Note Hashes"
              count={noteHashes.length}
              defaultOpen={false}
            >
              <Card style={{ padding: 0, overflow: "hidden" }}>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {noteHashes.map((n) => (
                        <tr key={n.id}>
                          <td>{n.position}</td>
                          <td>
                            <Mono style={{ fontSize: "10px" }}>{n.value}</Mono>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </Card>
            </Collapsible>
          )}
          {nullifiers.length > 0 && (
            <Collapsible
              title="Nullifiers"
              count={nullifiers.length}
              defaultOpen={false}
            >
              <Card style={{ padding: 0, overflow: "hidden" }}>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nullifiers.map((n) => (
                        <tr key={n.id}>
                          <td>{n.position}</td>
                          <td>
                            <Mono style={{ fontSize: "10px" }}>{n.value}</Mono>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </Card>
            </Collapsible>
          )}
          <SlotTimelines networkId={selectedNetwork} hash={hash ?? ""} />
          {l2ToL1Messages.length > 0 && (
            <L2ToL1MessagesSection messages={l2ToL1Messages} />
          )}
          {privateLogDetails.length > 0 && (
            <PrivateLogsSection logs={privateLogDetails} />
          )}
          {publicLogDetails.length > 0 && (
            <PublicLogsSection logs={publicLogDetails} />
          )}
          {contractClassLogDetails.length > 0 && (
            <ContractClassLogsSection logs={contractClassLogDetails} />
          )}
        </>
      )}

      {activeTab === "public" && (
        <>
          <PublicAddressesSection
            addresses={publicAddresses}
            feePayer={tx.feePayer}
          />
          {publicCalls.length > 0 && (
            <>
              <h3
                style={{
                  fontSize: theme.fontSize.md,
                  fontWeight: 600,
                  marginBottom: theme.spacing.sm,
                  color: theme.colors.text,
                }}
              >
                Public Calls ({publicCalls.length})
              </h3>
              <PublicCallsSection
                calls={publicCalls}
                labeledAddresses={labeledAddresses}
              />
            </>
          )}
        </>
      )}

      {activeTab === "similar" && featureVector && (
        <SimilarGrid>
          {sortedSimilarTxs.map((stx) => {
            const outlierColor =
              stx.outlierScore != null && stx.outlierScore > 0.5
                ? theme.colors.danger
                : stx.outlierScore != null && stx.outlierScore > 0.2
                  ? theme.colors.warning
                  : theme.colors.success;
            return (
              <SimilarTxCard key={stx.txHash}>
                <SimilarTxHeader>
                  <Link
                    to={`/${selectedNetwork}/tx/${stx.txHash}`}
                    style={{
                      color: theme.colors.primary,
                      textDecoration: "none",
                    }}
                  >
                    <Mono style={{ fontSize: "10px" }}>
                      {abbreviateHex(stx.txHash)}
                    </Mono>
                  </Link>
                  <StatusBadge status={stx.status} />
                  {stx.outlierScore != null && (
                    <Badge color={outlierColor} style={{ fontSize: "9px" }}>
                      {(stx.outlierScore * 100).toFixed(1)}%
                    </Badge>
                  )}
                </SimilarTxHeader>
                <FingerprintCompare
                  vectorA={featureVector}
                  vectorB={similarTxToVector(stx)}
                  labelA="This TX"
                  labelB={abbreviateHex(stx.txHash)}
                  compact
                />
              </SimilarTxCard>
            );
          })}
        </SimilarGrid>
      )}
    </PageContainer>
  );
}
